import { useState } from "react";
import { toast } from "sonner";
import { Wifi, WifiOff, RefreshCw, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { displayCurrency, displayCurrencyNumber, fromDisplayCurrency } from "../utils/pricing";
import { IntegrationsTabs } from "./IntegrationsHeyGen";
import { useEffect } from "react";

// Strip vendor prefixes from any backend-supplied model.name so the
// UI never leaks third-party brands. The regex tokens are matched
// case-insensitively and only at the start of the string.
const cleanModelName = (name) => {
    const banned = ["Runby", "Runway", "HeyGen", "OpenAI", "GPT",
                    "Anthropic", "Claude", "Gemini", "Google", "GenAI",
                    "Claude", "Gemini", "Anthropic"];
    const rx = new RegExp("^(" + banned.join("|") + ")\\s+", "i");
    return (name || "").replace(rx, "").trim();
};

const STATUS_STYLE = {
    OK: { bg: "bg-emerald-50", fg: "text-emerald-700", icon: CheckCircle2 },
    NO_KEY: { bg: "bg-neutral-100", fg: "text-neutral-700", icon: WifiOff },
    ERROR: { bg: "bg-red-50", fg: "text-red-700", icon: AlertTriangle },
};

export default function IntegrationsRunway() {
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/integrations/runway");
            setView(data);
            // Keep form state in RAW units (matching backend); every
            // dollar render routes through ``displayCurrency`` and every
            // dollar input through ``displayCurrencyNumber`` /
            // ``fromDisplayCurrency`` so the 1.5x multiplier is
            // consistently applied at the presentation layer only.
            setForm(data.settings);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/integrations/runway/test");
            setView(data);
            if (data.connection_status === "OK") toast.success("Cinematic Engine connection OK");
            else if (data.connection_status === "NO_KEY") toast.error("Cinematic Engine API key not configured");
            else toast.error(`Cinematic Engine error: ${data.connection_error}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            // form state is RAW; ship straight to backend.
            const { data } = await api.put("/integrations/runway", form);
            setView(data);
            setForm(data.settings);
            toast.success("Settings saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (loading || !view || !form) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

    const StyleCfg = STATUS_STYLE[view.connection_status] || STATUS_STYLE.NO_KEY;
    const StatusIcon = StyleCfg.icon;

    return (
        <div data-testid="runway-integration-page" className="space-y-6">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Admin · Integrations</div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">Cinematic Engine</h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    Cinematic and image-to-video generation. All calls happen server-side; the API key never
                    reaches the browser.
                </p>
            </div>

            <IntegrationsTabs />

            {/* Connection card */}
            <div className={`rounded-lg border border-neutral-200 bg-white p-5`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${StyleCfg.bg}`}>
                            <StatusIcon size={18} className={StyleCfg.fg} />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-neutral-900">Connection</div>
                            <div className={`text-xs ${StyleCfg.fg}`} data-testid="runway-connection-status">
                                {view.connection_status}
                                {view.connection_error ? ` · ${view.connection_error}` : ""}
                            </div>
                            {view.last_checked_at && (
                                <div className="mono text-[10px] text-neutral-400 mt-0.5">
                                    Last checked {new Date(view.last_checked_at).toLocaleString()}
                                </div>
                            )}
                        </div>
                    </div>
                    <Button variant="outline" onClick={test} disabled={testing} data-testid="runway-test-btn">
                        <RefreshCw size={13} className={`mr-1.5 ${testing ? "animate-spin" : ""}`} />
                        Test connection
                    </Button>
                </div>
                {view.connection_status === "NO_KEY" && (
                    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                        <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                        <div>
                            The Cinematic Engine API key is not configured on the server. Contact support or
                            add it to the backend environment and restart the backend. All Cinematic Engine
                            endpoints will return <span className="mono">503</span> until then; the rest of
                            the platform keeps working.
                        </div>
                    </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="rounded-md border border-neutral-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Month-to-date spend</div>
                        <div className="mono text-lg text-neutral-900 mt-1" data-testid="runway-mtd-spend">${displayCurrency(view.month_to_date_spend_usd)}</div>
                    </div>
                    <div className="rounded-md border border-neutral-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Today's spend</div>
                        <div className={`mono text-lg mt-1 ${view.daily_spend_usd >= form.org_daily_budget_usd ? "text-amber-700" : "text-neutral-900"}`}>
                            ${displayCurrency(view.daily_spend_usd)}
                            <span className="text-xs text-neutral-500"> / ${displayCurrency(form.org_daily_budget_usd)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Settings */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5 space-y-4">
                <div className="text-sm font-semibold text-neutral-900">Defaults</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <Label className="text-xs">Premium model (Type B cinematic, Type A trainer)</Label>
                        <Select value={form.default_premium_model_id} onValueChange={(v) => setForm({ ...form, default_premium_model_id: v })}>
                            <SelectTrigger data-testid="premium-model-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {view.available_models.map((m) => <SelectItem key={m.id} value={m.id}>{cleanModelName(m.name)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Draft model (image-to-video, cheaper)</Label>
                        <Select value={form.default_draft_model_id} onValueChange={(v) => setForm({ ...form, default_draft_model_id: v })}>
                            <SelectTrigger data-testid="draft-model-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {view.available_models.map((m) => <SelectItem key={m.id} value={m.id}>{cleanModelName(m.name)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Aspect ratio</Label>
                        <Select value={form.default_aspect_ratio} onValueChange={(v) => setForm({ ...form, default_aspect_ratio: v })}>
                            <SelectTrigger data-testid="aspect-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["1280:720", "720:1280", "1104:832", "960:960", "832:1104", "1584:672"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Default clip duration (s)</Label>
                        <Input type="number" min={2} max={10} value={form.default_clip_duration_s}
                            onChange={(e) => setForm({ ...form, default_clip_duration_s: parseInt(e.target.value || 6, 10) })}
                            data-testid="duration-input" />
                    </div>
                    <div>
                        <Label className="text-xs">Max per-module video budget ($)</Label>
                        <Input type="number" min={0} step={0.5}
                            value={displayCurrencyNumber(form.max_video_budget_usd)}
                            onChange={(e) => setForm({ ...form, max_video_budget_usd: fromDisplayCurrency(parseFloat(e.target.value || 5)) })}
                            data-testid="budget-input" />
                    </div>
                    <div>
                        <Label className="text-xs">Max generations per scene</Label>
                        <Input type="number" min={1} max={10} value={form.max_generations_per_scene}
                            onChange={(e) => setForm({ ...form, max_generations_per_scene: parseInt(e.target.value || 3, 10) })}
                            data-testid="per-scene-input" />
                    </div>
                    <div>
                        <Label className="text-xs">Preferred mode</Label>
                        <Select value={form.preferred_generation_mode} onValueChange={(v) => setForm({ ...form, preferred_generation_mode: v })}>
                            <SelectTrigger data-testid="preferred-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["PREMIUM", "STANDARD", "DRAFT"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Org daily budget ($) — soft warn at 1×, hard block at 2×</Label>
                        <Input type="number" min={0} step={1}
                            value={displayCurrencyNumber(form.org_daily_budget_usd)}
                            onChange={(e) => setForm({ ...form, org_daily_budget_usd: fromDisplayCurrency(parseFloat(e.target.value || 20)) })}
                            data-testid="daily-budget-input" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button onClick={save} disabled={saving} data-testid="save-runway-settings" className="bg-neutral-900 text-white hover:bg-neutral-800">
                        {saving ? "Saving…" : "Save settings"}
                    </Button>
                </div>
            </div>

            {/* Models catalog */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5">
                <div className="text-sm font-semibold text-neutral-900 mb-3">Available models</div>
                <div className="grid grid-cols-2 gap-3">
                    {view.available_models.map((m) => (
                        <div key={m.id} className="rounded-md border border-neutral-200 p-3">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-neutral-900">{cleanModelName(m.name)}</div>
                                <div className="mono text-xs text-neutral-500">${displayCurrency(m.price_per_second_usd)}/s</div>
                            </div>
                            <div className="mt-1 text-[11px] text-neutral-500">
                                Modes: {m.supported_modes.join(", ")} · Ratios: {m.aspect_ratios.length} · Duration {m.supported_durations[0]}-{m.supported_durations[m.supported_durations.length - 1]}s
                            </div>
                            {m.notes && <div className="mt-1 text-[11px] text-neutral-400 italic">{m.notes}</div>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
